import React from 'react';
import Footer from './Footer';

const PrivacyPolicy = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';

  const styles = {
    container: {
      minHeight: '100vh',
      background: isDark ? '#0f0f1e' : '#f9fafb',
      color: isDark ? '#e0e0e0' : '#1f2937',
      paddingTop: '80px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 20px',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '10px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    lastUpdated: {
      fontSize: '0.9rem',
      color: isDark ? '#9ca3af' : '#6b7280',
      marginBottom: '40px',
    },
    section: {
      marginBottom: '40px',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '15px',
      color: isDark ? '#ffffff' : '#1f2937',
    },
    paragraph: {
      fontSize: '1rem',
      lineHeight: '1.8',
      marginBottom: '15px',
      color: isDark ? '#d0d0d0' : '#4b5563',
    },
    list: {
      marginLeft: '20px',
      marginBottom: '15px',
    },
    listItem: {
      fontSize: '1rem',
      lineHeight: '1.8',
      marginBottom: '10px',
      color: isDark ? '#d0d0d0' : '#4b5563',
    },
    link: {
      color: '#667eea',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.paragraph}>
            Recapify ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Chrome extension and web application.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
          <p style={styles.paragraph}>We collect the following types of information:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Account Information:</strong> Email address, name, and authentication credentials when you register</li>
            <li style={styles.listItem}><strong>Recording Data:</strong> Audio/video recordings you create, including metadata (title, date, duration)</li>
            <li style={styles.listItem}><strong>Transcriptions:</strong> Text transcriptions generated from your recordings</li>
            <li style={styles.listItem}><strong>Tab Information:</strong> URLs and titles of browser tabs you choose to record</li>
            <li style={styles.listItem}><strong>Usage Data:</strong> Information about how you use our service (features accessed, storage used)</li>
            <li style={styles.listItem}><strong>Payment Information:</strong> Payment details processed securely through Stripe (we do not store full credit card numbers)</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>
          <p style={styles.paragraph}>We use your information to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide and maintain our recording and transcription services</li>
            <li style={styles.listItem}>Generate AI-powered summaries and enable semantic search</li>
            <li style={styles.listItem}>Process payments and send invoices</li>
            <li style={styles.listItem}>Communicate with you about your account and service updates</li>
            <li style={styles.listItem}>Improve our services and develop new features</li>
            <li style={styles.listItem}>Ensure security and prevent fraud</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Data Storage and Security</h2>
          <p style={styles.paragraph}>
            Your data is stored securely on Amazon Web Services (AWS) infrastructure:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Recordings:</strong> Stored securely in the cloud with encryption at rest</li>
            <li style={styles.listItem}><strong>User Data:</strong> Managed through secure authentication services</li>
            <li style={styles.listItem}><strong>Transcriptions:</strong> Processed using professional transcription services</li>
            <li style={styles.listItem}><strong>AI Summaries:</strong> Generated using enterprise AI services</li>
            <li style={styles.listItem}><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS) and at rest</li>
          </ul>
          <p style={styles.paragraph}>
            We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Data Sharing and Third Parties</h2>
          <p style={styles.paragraph}>
            We do not sell, trade, or rent your personal information. We share data only with:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Cloud Service Providers:</strong> For secure hosting, storage, transcription, and AI processing</li>
            <li style={styles.listItem}><strong>Payment Processor:</strong> Stripe for secure payment processing</li>
          </ul>
          <p style={styles.paragraph}>
            <strong>Important:</strong> Your recordings and transcriptions are never used to train AI models. Your content remains private and is only processed to provide you with summaries and search functionality.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Your Rights and Choices</h2>
          <p style={styles.paragraph}>You have the right to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Access:</strong> View all your recordings and data through the web application</li>
            <li style={styles.listItem}><strong>Delete:</strong> Remove individual recordings or close your account entirely</li>
            <li style={styles.listItem}><strong>Export:</strong> Download your recordings and transcriptions</li>
            <li style={styles.listItem}><strong>Control:</strong> Choose transcription methods and manage storage</li>
          </ul>
          <p style={styles.paragraph}>
            To exercise these rights or for data-related requests, contact us at <a href="mailto:support@recapifyapp.com" style={styles.link}>support@recapifyapp.com</a>.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Data Retention</h2>
          <p style={styles.paragraph}>
            We retain your data for as long as your account is active. When you close your account, all recordings, transcriptions, and personal data are permanently deleted within 24 hours. A final invoice will be sent before deletion.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Chrome Extension Permissions</h2>
          <p style={styles.paragraph}>Our Chrome extension requires the following permissions:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>storage:</strong> To save your settings and preferences locally</li>
            <li style={styles.listItem}><strong>tabCapture:</strong> To record audio/video from browser tabs</li>
            <li style={styles.listItem}><strong>tabs:</strong> To access tab information (URL, title) for recordings</li>
            <li style={styles.listItem}><strong>activeTab:</strong> To interact with the current tab during recording</li>
            <li style={styles.listItem}><strong>scripting:</strong> To inject recording controls into web pages</li>
          </ul>
          <p style={styles.paragraph}>
            These permissions are used solely to provide recording functionality. We do not track your browsing activity or access tabs you're not actively recording.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Local Storage and Data Persistence</h2>
          <p style={styles.paragraph}>
            We use browser local storage and session storage to provide essential functionality:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Authentication Tokens:</strong> Stored locally to keep you signed in (localStorage or sessionStorage based on your "Remember Me" preference)</li>
            <li style={styles.listItem}><strong>User Preferences:</strong> Theme settings and interface preferences</li>
            <li style={styles.listItem}><strong>Recording State:</strong> Active recording information and buffered audio data (Chrome extension only)</li>
            <li style={styles.listItem}><strong>Payment Status Cache:</strong> To reduce server requests and improve performance</li>
          </ul>
          <p style={styles.paragraph}>
            This data is stored only on your device and is not transmitted to third parties. You can clear this data at any time through your browser settings or by signing out of your account. We do not use tracking cookies or third-party analytics.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Children's Privacy</h2>
          <p style={styles.paragraph}>
            Recapify is not intended for users under 18 years of age. We do not knowingly collect information from children.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Changes to This Policy</h2>
          <p style={styles.paragraph}>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through the application. Continued use of our services after changes constitutes acceptance of the updated policy.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Contact Us</h2>
          <p style={styles.paragraph}>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p style={styles.paragraph}>
            Email: <a href="mailto:support@recapifyapp.com" style={styles.link}>support@recapifyapp.com</a>
          </p>
        </div>
      </div>
      <Footer theme={theme} />
    </div>
  );
};

export default PrivacyPolicy;
